/**
 * ProductsTab — catalogue produits & services du dossier : recherche, création / modification.
 */
import { useCallback, useEffect, useState } from "react";
import { Package, Plus, Search } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Empty, Pill, accLabel, errMsg, inputCls, money, useGlBase } from "../comptabilite/_shared";
import { PRODUCT_KIND_LABEL } from "./_books";
import ProductDialog from "./ProductDialog";

export default function ProductsTab({ accounts }) {
  const base = useGlBase();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null); // null | "new" | product

  const load = useCallback(async () => {
    try { const r = await api.get(`${base}/products`, { params: { q: q || undefined } }); setData(r.data); }
    catch (e) { toast.error(errMsg(e, "Chargement du catalogue impossible")); }
  }, [base, q]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="products-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code ou description…" className={`${inputCls} pl-9 w-64`} data-testid="products-search" />
          </div>
          {data && <span className="text-xs text-slate-500">{items.length} produit(s) / service(s)</span>}
        </div>
        <Btn icon={Plus} onClick={() => setDialog("new")} disabled={!accounts.length} title={!accounts.length ? "Installe d'abord le plan comptable" : ""} testId="products-new-btn">Nouveau produit / service</Btn>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 w-28">Code</th>
              <th className="text-left px-2 py-2">Description</th>
              <th className="text-left px-2 py-2 w-20 hidden md:table-cell">Type</th>
              <th className="text-right px-2 py-2 w-28">Prix de vente</th>
              <th className="text-right px-2 py-2 w-28 hidden lg:table-cell">Coût</th>
              <th className="text-left px-2 py-2 hidden lg:table-cell">Compte de vente</th>
              <th className="text-left px-2 py-2 hidden xl:table-cell">Compte d'achat</th>
              <th className="text-left px-2 py-2 w-24">Taxes</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((p) => (
              <tr key={p.id} className={`hover:bg-teal-50/40 cursor-pointer ${p.is_active === false ? "text-slate-400" : ""}`} onClick={() => setDialog(p)} data-testid={`product-row-${p.id}`}>
                <td className="px-3 py-2 font-mono text-xs font-semibold text-teal-800">{p.code}</td>
                <td className="px-2 py-2"><span className="font-medium">{p.name}</span>{p.is_active === false && <Pill>Inactif</Pill>}{p.unit && <span className="text-[11px] text-slate-400 ml-1">/ {p.unit}</span>}</td>
                <td className="px-2 py-2 text-xs text-slate-500 hidden md:table-cell">{PRODUCT_KIND_LABEL[p.kind] || p.kind}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums" data-testid={`product-price-${p.id}`}>{money(p.sale_price)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-500 hidden lg:table-cell">{p.cost ? money(p.cost) : "—"}</td>
                <td className="px-2 py-2 text-xs text-slate-500 hidden lg:table-cell">{p.sale_account_id ? accLabel(accounts, p.sale_account_id) : "—"}</td>
                <td className="px-2 py-2 text-xs text-slate-500 hidden xl:table-cell">{p.purchase_account_id ? accLabel(accounts, p.purchase_account_id) : "—"}</td>
                <td className="px-2 py-2"><Pill cls={p.taxable === false ? "bg-slate-100 text-slate-600 border-slate-200" : "bg-indigo-50 text-indigo-700 border-indigo-200"}>{p.taxable === false ? "Non taxable" : "TPS/TVQ"}</Pill></td>
              </tr>
            ))}
            {data && !items.length && <tr><td colSpan={8}><Empty icon={Package} text="Aucun produit ou service." testId="products-empty"><Btn icon={Plus} onClick={() => setDialog("new")} disabled={!accounts.length} testId="products-empty-new-btn">Créer un produit / service</Btn></Empty></td></tr>}
          </tbody>
        </table>
      </div>

      {dialog && <ProductDialog product={dialog === "new" ? null : dialog} accounts={accounts} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}
